import { POSTCODES_IO_BASE } from '../constants';
import type { PostcodeLookupResult } from '../types';

const cache = new Map<string, PostcodeLookupResult>();

/**
 * Look up a UK postcode via postcodes.io (free, no API key).
 * Returns null if the postcode is invalid or the network call fails.
 */
export async function lookupPostcode(
  postcode: string
): Promise<PostcodeLookupResult | null> {
  const normalised = postcode.trim().toUpperCase().replace(/\s+/g, ' ');
  if (!normalised) return null;
  if (cache.has(normalised)) return cache.get(normalised)!;

  try {
    const r = await fetch(
      `${POSTCODES_IO_BASE}/postcodes/${encodeURIComponent(normalised)}`
    );
    if (!r.ok) return null;
    const j = await r.json();
    if (j.status !== 200 || !j.result) return null;
    const out: PostcodeLookupResult = {
      postcode: j.result.postcode,
      longitude: j.result.longitude,
      latitude: j.result.latitude,
      admin_district: j.result.admin_district,
    };
    cache.set(normalised, out);
    return out;
  } catch (err) {
    console.warn('postcode lookup failed', err);
    return null;
  }
}

/**
 * Loose UK postcode validator. Accepts the formats we care about
 * (e.g. "SW1A 1AA", "CM1 1AB", "E1 6AN"). Used for client-side feedback
 * before hitting postcodes.io.
 */
export function isLikelyUkPostcode(input: string): boolean {
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(input.trim());
}
